import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  FIXED_THEMES,
  getRotatingTheme,
  type ThemeOption,
} from "./themes";

export type { ThemeOption };
export { FIXED_THEMES as THEME_OPTIONS };
export const SURPRISE_THEME_ID = "surprise";

interface ThemeSelectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (themeId: string | null) => void;
  currentTheme: string | null;
}

/**
 * Bottom sheet that lets the user choose a weekly theme.
 *
 * Shows 4 options:
 *   1. Express & léger (light)
 *   2. Classiques réconfortants (comfort)
 *   3. Surprise-moi (surprise)
 *   4. A randomly-picked theme from the rotating catalogue — changes each open.
 */
export function ThemeSelectionSheet({
  open,
  onOpenChange,
  onSelect,
  currentTheme,
}: ThemeSelectionSheetProps) {
  const [rotatingTheme, setRotatingTheme] = useState<ThemeOption>(() =>
    getRotatingTheme(),
  );

  // Pick a fresh random theme each time the sheet opens
  useEffect(() => {
    if (open) setRotatingTheme(getRotatingTheme());
  }, [open]);

  const options: ThemeOption[] = [...FIXED_THEMES, rotatingTheme];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-hair bg-background pb-[env(safe-area-inset-bottom)] shadow-soft-warm-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 sm:bottom-auto sm:top-1/2 sm:max-w-lg sm:-translate-y-1/2 sm:border sm:rounded-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-hair px-6 py-4">
            <Dialog.Title className="font-serif text-[15px] font-medium">
              Thème de la semaine
            </Dialog.Title>
            <button
              aria-label="Fermer"
              onClick={() => onOpenChange(false)}
              className="text-foreground/60 hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          {/* Description */}
          <div className="px-6 pt-5 pb-3">
            <p className="text-[13px] text-ink-60 leading-relaxed">
              Choisissez une ambiance culinaire pour votre prochaine session de
              batch cooking. Le thème guidera la sélection des recettes de la
              semaine.
            </p>
          </div>

          {/* Theme options */}
          <div className="space-y-2 px-6 pb-5">
            {options.map((option) => {
              const selected =
                option.id === "surprise"
                  ? currentTheme === "surprise" || currentTheme === null
                  : currentTheme === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onSelect(option.id);
                    onOpenChange(false);
                  }}
                  className={`flex w-full items-start gap-4 rounded-pill border px-4 py-3.5 text-left transition-colors ${
                    selected
                      ? "border-accent bg-accent/10"
                      : "border-hair bg-background hover:border-ink-30"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${
                      selected
                        ? "bg-accent text-background"
                        : "bg-wash text-ink-50"
                    }`}
                  >
                    {option.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-medium text-foreground">
                      {option.label}
                    </div>
                    <div className="text-[12px] text-ink-50">
                      {option.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 border-t border-hair px-6 py-4">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Garder le choix actuel
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
